import { Link, useLocation } from "wouter";
import { Home, Menu, Package, ScanLine, ShoppingBag } from "lucide-react";
import { useMe } from "@/lib/auth";
import { useOptionalSidebar } from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";
import { preloadRoute } from "@/lib/app-performance";
import {
  MOBILE_DESTINATIONS,
  canUseMobileDestination,
  isMobileDestinationActive,
  isMobileMoreActive,
  type MobileDestination,
} from "@/lib/mobile-navigation";

const ICONS: Record<MobileDestination["key"], typeof Home> = {
  home: Home,
  sales: ShoppingBag,
  stock: Package,
  packing: ScanLine,
};

type MobileBottomNavProps = {
  location?: string;
  onMore?: () => void;
  packingBadge?: number;
};

export function MobileBottomNav({
  location: suppliedLocation,
  onMore,
  packingBadge = 0,
}: MobileBottomNavProps = {}) {
  const [routeLocation] = useLocation();
  const location = suppliedLocation ?? routeLocation;
  const { has } = useMe();
  const sidebar = useOptionalSidebar();
  const openMobile = sidebar?.openMobile ?? false;
  const moreActive = isMobileMoreActive(location);
  const moreHighlighted = moreActive || openMobile;

  return (
    <nav
      className="mobile-bottom-nav fixed inset-x-0 bottom-0 z-40 border-t border-border/80 bg-background/95 backdrop-blur-xl md:hidden"
      aria-label="Primary mobile navigation"
      data-testid="mobile-bottom-nav"
    >
      <ul className="mx-auto grid h-[4.25rem] max-w-xl grid-cols-5 px-1">
        {MOBILE_DESTINATIONS.map((item) => {
          const Icon = ICONS[item.key];
          const active = isMobileDestinationActive(location, item);
          const allowed = canUseMobileDestination(has, item);
          const badge = item.key === "packing" ? packingBadge : 0;
          if (!allowed) {
            return (
              <li key={item.key} className="flex min-w-0">
                <span
                  className="flex min-h-11 flex-1 flex-col items-center justify-center gap-0.5 px-1 text-[10px] font-medium text-muted-foreground/40"
                  aria-label={`${item.label} unavailable`}
                  aria-disabled="true"
                  data-testid={`mobile-nav-${item.key}-unavailable`}
                >
                  <Icon className="h-[1.15rem] w-[1.15rem]" aria-hidden />
                  <span className="truncate">{item.label}</span>
                </span>
              </li>
            );
          }
          return (
            <li key={item.key} className="flex min-w-0">
              <Link
                href={item.href}
                className={cn(
                  "relative flex min-h-11 flex-1 flex-col items-center justify-center gap-0.5 rounded-xl px-1 text-[10px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset",
                  active ? "text-brand-gold" : "text-muted-foreground hover:text-foreground",
                )}
                aria-current={active ? "page" : undefined}
                data-testid={`mobile-nav-${item.key}`}
                onMouseEnter={() => { if (item.href !== "/") void preloadRoute(item.href); }}
                onFocus={() => { if (item.href !== "/") void preloadRoute(item.href); }}
                onTouchStart={() => { if (item.href !== "/") void preloadRoute(item.href); }}
              >
                <span
                  className={cn(
                    "absolute top-1 h-1 w-7 rounded-full transition-opacity motion-reduce:transition-none",
                    active ? "bg-brand-gold opacity-100" : "opacity-0",
                  )}
                  aria-hidden
                />
                <span className={cn("flex h-7 w-9 items-center justify-center rounded-xl transition-colors motion-reduce:transition-none", active && "bg-brand-gold/12")}>
                  <Icon className="h-[1.15rem] w-[1.15rem]" strokeWidth={active ? 2.4 : 1.9} aria-hidden />
                  {badge > 0 ? <span className="absolute right-[calc(50%-1.1rem)] top-1 min-w-4 rounded-full bg-destructive px-1 text-[9px] leading-4 text-destructive-foreground">{badge > 99 ? "99+" : badge}</span> : null}
                </span>
                <span className="truncate">{item.label}</span>
              </Link>
            </li>
          );
        })}
        <li className="flex min-w-0">
          <button
            type="button"
            onClick={() => {
              if (onMore) {
                onMore();
              } else {
                sidebar?.setOpenMobile(!openMobile);
              }
            }}
            className={cn(
              "relative flex min-h-11 flex-1 flex-col items-center justify-center gap-0.5 rounded-xl px-1 text-[10px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset",
              moreHighlighted ? "text-brand-gold" : "text-muted-foreground hover:text-foreground",
            )}
            aria-label={openMobile ? "Close more navigation" : "Open more navigation"}
            aria-current={moreActive ? "page" : undefined}
            aria-expanded={openMobile}
            data-testid="mobile-nav-more"
          >
            <span
              className={cn(
                "absolute top-1 h-1 w-7 rounded-full transition-opacity motion-reduce:transition-none",
                moreHighlighted ? "bg-brand-gold opacity-100" : "opacity-0",
              )}
              aria-hidden
            />
            <span className={cn("flex h-7 w-9 items-center justify-center rounded-xl transition-colors motion-reduce:transition-none", moreHighlighted && "bg-brand-gold/12")}>
              <Menu className="h-[1.15rem] w-[1.15rem]" strokeWidth={moreHighlighted ? 2.4 : 1.9} aria-hidden />
            </span>
            <span>More</span>
          </button>
        </li>
      </ul>
    </nav>
  );
}